import { observer } from "mobx-react";
import { Redirect, useHistory } from "react-router-dom";

import { useAlert } from "react-alert";
import { Header } from "../../components/Header";
import testResultStore from "../../stores/TestResultStore";
import { HOME, TEST_PHOTO } from "../../constants/routes";
import { ReactComponent as IconPhoto } from "../../assets/icons/camera.svg";
import translationsStore from "../../stores/TranslationsStore";
import {
  ALREADY_VERIFIED,
  GO_BACK,
  PENDING,
  TEST_PHOTO_RETAKE,
  TEST_PHOTO_TAKE_IMAGE,
  VERIFY_LATER_BUTTON,
  VERIFY_TEST_NEGATIVE_BUTTON,
  VERIFY_TEST_POSITIVE_BUTTON,
} from "../../constants/translationKeys";
import moment from "moment";
import { getHistory, getPrivateKeyPem, getSupervisorId, saveHistory } from "../../utils/localStorage";
import {
  encodeString,
  importPrivateKeyPem,
  stringifyBuffer,
} from "../../helpers/crypto";
import { verifySupervision } from "../../api/testResult";
import "./TestResultPreview.scss";

const TestResultPreviewPage = () => {
  const testResult = testResultStore.testResult;
  const testeeFullName = testResultStore.testeeFullName;
  const supervisionId = testResultStore.supervisionId;
  const history = useHistory();

  const alert = useAlert();
  if (!testResult) return <Redirect to={HOME} />;

  const isVerified = () => {
    return (
      testResult?.supervision && testResult.supervision.status !== "PENDING"
    );
  };

  const verifyTest = (result: "NEGATIVE" | "POSITIVE") => {
    signAndVerifySupervision(result);
  };

  const signAndVerifySupervision = async (result: "NEGATIVE" | "POSITIVE") => {
    const supervisorId = getSupervisorId();
    let resultImage: string | null = testResult.resultPhoto;
    const supervision: {
      supervisor_id: string;
      supervision_id: string;
      status: "POSITIVE" | "NEGATIVE";
      result_image: string | null;
      signed: string;
    } = {
      supervisor_id: supervisorId!,
      supervision_id: supervisionId!,
      status: result,
      result_image: resultImage,
      signed: "",
    };
    const privateKey = await importPrivateKeyPem(getPrivateKeyPem()!);
    const signed = await crypto.subtle.sign(
      "RSASSA-PKCS1-v1_5",
      privateKey,
      encodeString(`${supervisorId};${supervisionId};${result};`)
    );
    supervision.signed = stringifyBuffer(signed);

    verifySupervision(supervision)
      .then(() => {
        alert.show("Success!", { type: "success" });
        history.replace(HOME);
        addToHistory(supervision);
      })
      .catch(() => {
        alert.show("Verification of the test failed.", { type: "error" });
      });
  };

  const addToHistory = async (supervision: any) => {
    if (!supervision.status) return;

    const data = {
      status: supervision.status,
      supervisor_id: supervision.supervisor_id,
      supervision_id: supervision.supervision_id,
      testee: testeeFullName,
      created_at: testResult?.createdAt,
      confirmed_at: Date.now()
    }

    const hist = await getHistory();
    const items = hist ? JSON.parse(hist) : [];
    items.push(data);
    saveHistory(JSON.stringify(items));
  }

  const translations = translationsStore.translations;
  return (
    <div className="layout-column flex">
      <Header />
      <div className="page-container flex">
        <h2>{testResult?.testMetadata.testName}</h2>

        <div className="test-status">
          {isVerified() ? (
            <p>{translations[ALREADY_VERIFIED] || "Already verified"}</p>
          ) : (
            <p>{translations[PENDING]}</p>
          )}
        </div>

        {testResult?.resultPhoto && (
          <div className="test-image">
            <img
              src={testResult.resultPhoto}
              width={322}
              height={94}
              alt="Test"
            />
          </div>
        )}
        {!isVerified() && (
          <button
            className={`button button-left-icon test-photo-btn ${
              testResult?.resultPhoto
                ? "outline-button-primary"
                : "filled-button"
            }`}
            onClick={() => history.push(TEST_PHOTO)}
          >
            <IconPhoto />
            {testResult?.resultPhoto
              ? translations[TEST_PHOTO_RETAKE]
              : translations[TEST_PHOTO_TAKE_IMAGE]}
          </button>
        )}

        <div className="test-info">
          <h3>{testeeFullName}</h3>
          <div className="test-date">
            {moment(testResult.createdAt).format("LLLL")}
          </div>
        </div>

        <div className="spacer"></div>

        <button
          className="button filled-button"
          disabled={isVerified()}
          onClick={() => verifyTest("NEGATIVE")}
        >
          {translations[VERIFY_TEST_NEGATIVE_BUTTON]}
        </button>

        <button
          className="button outline-button-error"
          disabled={isVerified()}
          onClick={() => verifyTest("POSITIVE")}
        >
          {translations[VERIFY_TEST_POSITIVE_BUTTON]}
        </button>

        <button
          className="button text-button"
          onClick={() => {
            history.replace(HOME);
          }}
        >
          {isVerified()
            ? translations[GO_BACK] || "Go back"
            : translations[VERIFY_LATER_BUTTON]}
        </button>
      </div>
    </div>
  );
};

export default observer(TestResultPreviewPage);
